import { UnitSystem } from '@trackman/baseball-shared-components';
import { Ruleset } from 'enums/Ruleset';
import type { IApiGameLevel, IApiLeague, IOrganization } from 'interfaces/team/IApiScheduleResponse';
import { ISelectOption } from 'interfaces/ui/ISelectOption';
import { action, computed, makeObservable, observable } from 'mobx';
import { unitConverters } from 'services/unitConverters';
import type { IStrikeZoneDefinition, OverrideStrikeZoneProperty } from '../interfaces/session/IStrikeZoneDefinition';
import { IStrikeZoneLocation, StrikeZoneLocation } from '../interfaces/session/IStrikeZoneDefinition';
import type { IPlaneConfiguration, PlaneAdjustmentKind } from '../interfaces/session/IStrikeZonePlaneConfiguration';
import { LogService } from '../services/data-services/LogService';
import { IFeatureConfigService } from '../services/FeatureConfigService';
import {
  createDefaultPlaneConfiguration,
  mapCalculationsToPlaneConfiguration,
  mapPlaneConfigurationToLocations,
} from '../services/helper-services/strikeZonePlanes';
import { BaseStore } from './BaseStore';
import { ContextualDataStore } from './ContextualDataStore';

export class LineupStore extends BaseStore {
  constructor(logService: LogService, contextualDataStore: ContextualDataStore, featureConfigService: IFeatureConfigService) {
    super(logService);
    this.contextualDataStore = contextualDataStore;
    this.featureConfigService = featureConfigService;
    makeObservable(this);
  }

  private contextualDataStore: ContextualDataStore;

  private featureConfigService: IFeatureConfigService;

  @observable defaultStrikeZone?: OverrideStrikeZoneProperty;

  @observable ruleset: Ruleset = Ruleset.americanLeague;

  @observable league: IApiLeague = {} as IApiLeague;

  @observable gameLevel: IApiGameLevel = {} as IApiGameLevel;

  @observable owningOrganization: IOrganization = {} as IOrganization;

  @observable strikeZone?: IStrikeZoneDefinition;

  @observable isVideoCaptureEnabled = false;

  @observable isPlayerTrackingEnabled = false;

  @observable overridedStrikeZone: OverrideStrikeZoneProperty = {} as OverrideStrikeZoneProperty;

  @observable metrics: UnitSystem = UnitSystem.Imperial;

  @observable planeConfiguration: IPlaneConfiguration[] = createDefaultPlaneConfiguration();

  @computed get isAnyStrikeZoneDifference() {
    if (this.overridedStrikeZone) {
      return Object.keys(this.overridedStrikeZone).some((key: any) => {
        const property = key as keyof OverrideStrikeZoneProperty;

        if (this.overridedStrikeZone && this.defaultStrikeZone) {
          if (property !== 'activeStrikeZone') {
            return Number(this.overridedStrikeZone[property]) !== Number(this.defaultStrikeZone[property]);
          }
        }
      });
    }

    return false;
  }

  @observable rulesets: ISelectOption[] = [
    { value: Ruleset.americanLeague, label: 'American League' },
    { value: Ruleset.nationalLeague, label: 'National League' },
    { value: Ruleset.scrimmage, label: 'Scrimmage' },
  ];

  @action async setGameLevel(gameLevel: IApiGameLevel) {
    this.gameLevel = gameLevel;
  }

  @action setLeague(league: IApiLeague) {
    this.league = league;
    this.setupOverridedStrikeZone();
    this.setupPlaneConfiguration();
  }

  @action setupPlaneConfiguration = () => {
    this.planeConfiguration = mapCalculationsToPlaneConfiguration(this.league.strikeZoneCalculationInformations);
  };

  @action togglePlane = (locationType: StrikeZoneLocation) => {
    this.planeConfiguration = this.planeConfiguration.map((plane) =>
      plane.locationType === locationType ? { ...plane, isEnabled: !plane.isEnabled } : plane
    );
  };

  @action setPlaneAdjustmentEnabled = (locationType: StrikeZoneLocation, kind: PlaneAdjustmentKind, isEnabled: boolean) => {
    this.planeConfiguration = this.planeConfiguration.map((plane) => {
      if (plane.locationType !== locationType) {
        return plane;
      }

      if (kind === 'offset') {
        return { ...plane, isOffsetEnabled: isEnabled, offsetY: isEnabled ? plane.offsetY : 0 };
      }

      return { ...plane, isExtensionEnabled: isEnabled, extension: isEnabled ? plane.extension : 0 };
    });
  };

  @action setPlaneAdjustmentValue = (locationType: StrikeZoneLocation, kind: PlaneAdjustmentKind, valueInInches: number) => {
    this.planeConfiguration = this.planeConfiguration.map((plane) => {
      if (plane.locationType !== locationType) {
        return plane;
      }

      return kind === 'offset' ? { ...plane, offsetY: valueInInches } : { ...plane, extension: valueInInches };
    });
  };

  getPlaneLocationsForSessionMetadata = (): IStrikeZoneLocation[] => mapPlaneConfigurationToLocations(this.planeConfiguration);

  @action setRuleset(ruleset?: Ruleset) {
    if (ruleset) {
      this.ruleset = ruleset;
    }
  }

  @action setOwningOrganization(owningOrganization: any) {
    this.owningOrganization = owningOrganization;
  }

  @action setStrikeZone(strikeZone: IStrikeZoneDefinition | undefined) {
    this.strikeZone = strikeZone;
  }

  @action setupOverridedStrikeZone = () => {
    this.defaultStrikeZone = {
      staticStrikeZoneBottom: this.league.strikeZones?.staticStrikeZone?.strikeZoneBottom,
      staticStrikeZoneTop: this.league.strikeZones?.staticStrikeZone?.strikeZoneTop,
      staticStrikeZoneWidth: this.league.strikeZones?.staticStrikeZone?.strikeZoneWidth,
      heightStrikeZoneBottomPercentage: this.league.strikeZones?.heightBasedStrikeZone?.strikeZoneBottomPercentage,
      heightStrikeZoneTopPercentage: this.league.strikeZones?.heightBasedStrikeZone?.strikeZoneTopPercentage,
      heightStrikeZoneWidth: this.league.strikeZones?.heightBasedStrikeZone?.strikeZoneWidth,
      topBottomStrikeZoneWidth: this.league.strikeZones?.topAndBottomStrikeZone?.strikeZoneWidth,
      ballDiameter: this.league.ballDiameter || null,
      activeStrikeZone: this.league.activeStrikeZone,
    };

    this.overridedStrikeZone = { ...this.defaultStrikeZone };
  };

  @action revertStrikeZoneChanges = () => {
    if (this.defaultStrikeZone) {
      this.overridedStrikeZone = {
        ...this.defaultStrikeZone,
        // Do not reset strike zone type
        activeStrikeZone: this.overridedStrikeZone.activeStrikeZone,
      };
    }
  };

  @action setIsVideoCaptureEnabled(value: boolean) {
    this.isVideoCaptureEnabled = this.featureConfigService.getConfig().isUploadVideoEnabled && value;
  }

  @action setIsPlayerTrackingEnabled(value: boolean) {
    this.isPlayerTrackingEnabled = this.featureConfigService.getConfig().isPlayerTrackingEnabled && value;
  }

  @action resetSessionMetaDataValues = () => {
    this.setLeague({ id: undefined, name: undefined });
    this.setGameLevel({ id: undefined, name: undefined });
    this.setOwningOrganization({ id: undefined, name: undefined });
    this.setRuleset(Ruleset.americanLeague);
    // TODO: use league strikezone
    this.setStrikeZone(this.contextualDataStore.defaultStrikeZone);
    this.setIsVideoCaptureEnabled(this.featureConfigService.getConfig().isUploadVideoEnabled);
    this.setIsPlayerTrackingEnabled(this.featureConfigService.getConfig().isPlayerTrackingEnabled);
    this.setMetrics(UnitSystem.Imperial);
  };

  @action overrideStrikeZoneDefinition = (property: keyof OverrideStrikeZoneProperty, value: any) => {
    if (property === 'activeStrikeZone') {
      this.overridedStrikeZone['activeStrikeZone'] = value;
      return;
    }

    if (this.overridedStrikeZone) {
      this.overridedStrikeZone[property] = value;
    } else {
      this.overridedStrikeZone = {} as OverrideStrikeZoneProperty;
      this.overridedStrikeZone[property] = value;
    }
  };

  @action setMetrics = (value: UnitSystem) => {
    this.metrics = value;
  };

  parseValuesToNumber = () => {
    Object.keys(this.overridedStrikeZone).forEach((key) => {
      const property = key as keyof OverrideStrikeZoneProperty;
      const value = this.overridedStrikeZone[property];

      if (property !== 'activeStrikeZone' && typeof value === 'string') {
        this.overridedStrikeZone[property] = Number(value);
      }
    });
  };

  getOverridedStrikeZoneForSessionMetadata = async () => {
    this.parseValuesToNumber();

    if (this.metrics === UnitSystem.Imperial) {
      return this.overridedStrikeZone;
    }

    const imperialValues = await this.recalculateStrikeZoneValues(UnitSystem.Imperial).then(() => {
      return this.overridedStrikeZone;
    });

    return imperialValues;
  };

  @action recalculateStrikeZoneValues = async (metrics: UnitSystem) => {
    if (!this.overridedStrikeZone || !this.defaultStrikeZone) {
      return;
    }

    if (metrics === UnitSystem.Imperial) {
      Object.keys(this.overridedStrikeZone).forEach((key: string) => {
        if (key === 'heightStrikeZoneBottomPercentage' || key === 'heightStrikeZoneTopPercentage' || key === 'activeStrikeZone') {
          return;
        }
        const value = this.overridedStrikeZone![key as keyof Omit<OverrideStrikeZoneProperty, 'activeStrikeZone'>];
        const defaultValue = this.defaultStrikeZone![key as keyof Omit<OverrideStrikeZoneProperty, 'activeStrikeZone'>];
        if (value) {
          this.overridedStrikeZone![key as keyof Omit<OverrideStrikeZoneProperty, 'activeStrikeZone'>] = Number(
            unitConverters.metersToInches(value)
          );
        }
        if (defaultValue) {
          this.defaultStrikeZone![key as keyof Omit<OverrideStrikeZoneProperty, 'activeStrikeZone'>] = Number(
            unitConverters.metersToInches(defaultValue)
          );
        }
      });
    } else {
      Object.keys(this.overridedStrikeZone).forEach((key: string) => {
        if (key === 'heightStrikeZoneBottomPercentage' || key === 'heightStrikeZoneTopPercentage' || key === 'activeStrikeZone') {
          return;
        }
        const value = this.overridedStrikeZone![key as keyof Omit<OverrideStrikeZoneProperty, 'activeStrikeZone'>];
        const defaultValue = this.defaultStrikeZone![key as keyof Omit<OverrideStrikeZoneProperty, 'activeStrikeZone'>];
        if (value) {
          this.overridedStrikeZone![key as keyof Omit<OverrideStrikeZoneProperty, 'activeStrikeZone'>] = Number(
            unitConverters.inchesToMeters(value)
          );
        }
        if (defaultValue) {
          this.defaultStrikeZone![key as keyof Omit<OverrideStrikeZoneProperty, 'activeStrikeZone'>] = Number(
            unitConverters.inchesToMeters(defaultValue)
          );
        }
      });
    }
  };
}
